import { useReducer, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';

interface IRenewalSettingsState { // LOCAL_TYPE_JUSTIFIED: reducer state only used in this hook
  renewalDays: number;
  smsFallbackDays: number;
  notificationMode: 'automatic' | 'manual';
  emailProvider: 'resend' | 'gmail';
  gmailUser: string;
  gmailAppPassword: string;
  websmsApiKey: string;
  senderName: string;
  emailTemplate: string;
  smsTemplate: string;
  companyEmailTemplate: string;
  isSaving: boolean;
}

type TSettingsAction = // LOCAL_TYPE_JUSTIFIED: reducer actions only used in this hook
  | { type: 'SET_RENEWAL_DAYS'; value: number }
  | { type: 'SET_SMS_FALLBACK_DAYS'; value: number }
  | { type: 'SET_NOTIFICATION_MODE'; value: 'automatic' | 'manual' }
  | { type: 'SET_EMAIL_PROVIDER'; value: 'resend' | 'gmail' }
  | { type: 'SET_GMAIL_USER'; value: string }
  | { type: 'SET_GMAIL_APP_PASSWORD'; value: string }
  | { type: 'SET_WEBSMS_API_KEY'; value: string }
  | { type: 'SET_SENDER_NAME'; value: string }
  | { type: 'SET_EMAIL_TEMPLATE'; value: string }
  | { type: 'SET_SMS_TEMPLATE'; value: string }
  | { type: 'SET_COMPANY_EMAIL_TEMPLATE'; value: string }
  | { type: 'SET_SAVING'; value: boolean }
  | { type: 'LOAD_DATA'; data: Partial<IRenewalSettingsState> };

const initialState: IRenewalSettingsState = {
  renewalDays: 30,
  smsFallbackDays: 3,
  notificationMode: 'automatic',
  emailProvider: 'resend',
  gmailUser: '',
  gmailAppPassword: '',
  websmsApiKey: '',
  senderName: '',
  emailTemplate: '',
  smsTemplate: '',
  companyEmailTemplate: '',
  isSaving: false,
};

function settingsReducer(
  state: IRenewalSettingsState,
  action: TSettingsAction
): IRenewalSettingsState {
  switch (action.type) {
    case 'SET_RENEWAL_DAYS':
      return { ...state, renewalDays: action.value };
    case 'SET_SMS_FALLBACK_DAYS':
      return { ...state, smsFallbackDays: action.value };
    case 'SET_NOTIFICATION_MODE':
      return { ...state, notificationMode: action.value };
    case 'SET_EMAIL_PROVIDER':
      return { ...state, emailProvider: action.value };
    case 'SET_GMAIL_USER':
      return { ...state, gmailUser: action.value };
    case 'SET_GMAIL_APP_PASSWORD':
      return { ...state, gmailAppPassword: action.value };
    case 'SET_WEBSMS_API_KEY':
      return { ...state, websmsApiKey: action.value };
    case 'SET_SENDER_NAME':
      return { ...state, senderName: action.value };
    case 'SET_EMAIL_TEMPLATE':
      return { ...state, emailTemplate: action.value };
    case 'SET_SMS_TEMPLATE':
      return { ...state, smsTemplate: action.value };
    case 'SET_COMPANY_EMAIL_TEMPLATE':
      return { ...state, companyEmailTemplate: action.value };
    case 'SET_SAVING':
      return { ...state, isSaving: action.value };
    case 'LOAD_DATA':
      return { ...state, ...action.data };
    default:
      return state;
  }
}

export interface IUseRenewalSettingsReturn { // LOCAL_TYPE_JUSTIFIED: return type for this hook — consumed by dialog and tab components
  state: IRenewalSettingsState;
  dispatch: React.Dispatch<TSettingsAction>;
  saveAll: () => Promise<void>;
  isLoading: boolean;
  smsConfigured: boolean;
}

export function useRenewalSettings(orgId: Id<'organisations'>): IUseRenewalSettingsReturn {
  const [state, dispatch] = useReducer(settingsReducer, initialState);

  const renewalDays = useQuery(api.insurance.renewalSettings.getRenewalDays, { orgId });
  const smsFallbackDays = useQuery(api.insurance.renewalSettings.getSmsFallbackDays, { orgId });
  const emailConfig = useQuery(api.insurance.renewalSettings.getEmailConfig, { orgId });
  const smsConfig = useQuery(api.insurance.renewalSettings.getSmsConfig, { orgId });
  const emailTemplate = useQuery(api.insurance.renewalSettings.getEmailTemplate, { orgId });
  const smsTemplate = useQuery(api.insurance.renewalSettings.getSmsTemplate, { orgId });
  const companyEmailTemplate = useQuery(
    api.insurance.renewalSettings.getCompanyEmailTemplate,
    { orgId }
  );
  const notificationMode = useQuery(
    api.insurance.renewalSettings.getNotificationMode,
    { orgId }
  );

  const setRenewalDays = useMutation(api.insurance.renewalSettings.setRenewalDays);
  const setSmsFallbackDays = useMutation(api.insurance.renewalSettings.setSmsFallbackDays);
  const setEmailConfig = useMutation(api.insurance.renewalSettings.setEmailConfig);
  const setSmsConfig = useMutation(api.insurance.renewalSettings.setSmsConfig);
  const setEmailTemplate = useMutation(api.insurance.renewalSettings.setEmailTemplate);
  const setSmsTemplate = useMutation(api.insurance.renewalSettings.setSmsTemplate);
  const setCompanyEmailTemplate = useMutation(
    api.insurance.renewalSettings.setCompanyEmailTemplate
  );
  const setNotificationMode = useMutation(api.insurance.renewalSettings.setNotificationMode);

  const isLoading =
    renewalDays === undefined ||
    smsFallbackDays === undefined ||
    emailConfig === undefined ||
    smsConfig === undefined ||
    emailTemplate === undefined ||
    smsTemplate === undefined ||
    companyEmailTemplate === undefined ||
    notificationMode === undefined;

  useEffect(() => {
    if (isLoading) return;
    dispatch({
      type: 'LOAD_DATA',
      data: {
        renewalDays: renewalDays ?? 30,
        smsFallbackDays: smsFallbackDays ?? 3,
        notificationMode: notificationMode ?? 'automatic',
        emailProvider: emailConfig?.provider ?? 'resend',
        gmailUser: emailConfig?.gmailUser ?? '',
        gmailAppPassword: '',
        websmsApiKey: '',
        senderName: smsConfig?.senderName ?? '',
        emailTemplate: emailTemplate ?? '',
        smsTemplate: smsTemplate ?? '',
        companyEmailTemplate: companyEmailTemplate ?? '',
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const saveAll = useCallback(async () => {
    dispatch({ type: 'SET_SAVING', value: true });
    try {
      await Promise.all([
        setRenewalDays({ orgId, days: state.renewalDays }),
        setSmsFallbackDays({ orgId, days: state.smsFallbackDays }),
        setNotificationMode({ orgId, mode: state.notificationMode }),
        setEmailConfig({
          orgId,
          provider: state.emailProvider,
          gmailUser: state.gmailUser || undefined,
          gmailAppPassword: state.gmailAppPassword || undefined,
        }),
        ...(state.websmsApiKey && state.senderName
          ? [setSmsConfig({ orgId, websmsApiKey: state.websmsApiKey, senderName: state.senderName })]
          : []),
        setEmailTemplate({ orgId, template: state.emailTemplate }),
        setSmsTemplate({ orgId, template: state.smsTemplate }),
        setCompanyEmailTemplate({ orgId, template: state.companyEmailTemplate }),
      ]);
    } finally {
      dispatch({ type: 'SET_SAVING', value: false });
    }
  }, [
    orgId,
    state,
    setRenewalDays,
    setSmsFallbackDays,
    setEmailConfig,
    setSmsConfig,
    setEmailTemplate,
    setSmsTemplate,
    setCompanyEmailTemplate,
    setNotificationMode,
  ]);

  const smsConfigured = smsConfig?.configured ?? false;

  return { state, dispatch, saveAll, isLoading, smsConfigured };
}
